// /welcome — where Stripe returns a subscriber (STRIPE_CHECKOUT_SUCCESS_URL).
//
// A full page load: all in-memory funnel state is gone, so everything here is
// rebuilt from localStorage (answers, claim link) + the ?session_id Stripe
// appends. The page is ASYNC-VERIFIED, never optimistic: it asks OUR server to
// confirm the payment against Stripe (which also provisions the entitlement if
// the webhook has not yet) before it says anything is active. "Paid but not
// provisioned" is the worst failure mode; this page is its second net.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { verifyCheckout, ApiError, RateLimited } from '../lib/api';
import { beginCheckout } from '../lib/checkout';
import { loadAnswers, loadClaimLink } from '../lib/session';
import { track, trackPurchase } from '../lib/analytics';
import { postEvent } from '../lib/postMessage';
import { GetTheApp } from '../screens/GetTheApp';
import { ClaimNotice } from '../claim/ClaimSuccess';

type Phase = 'checking' | 'active' | 'open' | 'expired' | 'processing' | 'missing' | 'error';

// A slow webhook usually lands within seconds; poll briefly before telling the
// patient to wait. 5 tries x 2.5s covers the common race without hammering.
const PROCESSING_RETRIES = 5;
const RETRY_DELAY_MS = 2500;

export function WelcomeApp() {
  const sessionId = new URLSearchParams(window.location.search).get('session_id') || '';
  const [phase, setPhase] = useState<Phase>(sessionId ? 'checking' : 'missing');
  const tracked = useRef(false);

  useEffect(() => {
    document.title = 'Welcome to Expy Health';
  }, []);

  useEffect(() => {
    if (!sessionId) return undefined;
    let cancelled = false;
    let attempts = 0;

    const check = async () => {
      try {
        const res = await verifyCheckout(sessionId);
        if (cancelled) return;
        if (res.status === 'processing' && attempts < PROCESSING_RETRIES) {
          attempts += 1;
          setTimeout(check, RETRY_DELAY_MS);
          return;
        }
        setPhase(res.status);
        if (res.status === 'active' && !tracked.current) {
          tracked.current = true;
          track('checkout_completed');
          postEvent('checkout_completed');
          const { value, currency } = subscriptionPrice(loadAnswers().country);
          trackPurchase(value, currency);
        }
      } catch (e) {
        if (cancelled) return;
        if (e instanceof RateLimited) {
          setTimeout(check, (e as RateLimited).retryAfterMs);
          return;
        }
        setPhase(e instanceof ApiError && e.status === 404 ? 'missing' : 'error');
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  if (phase === 'checking') {
    return (
      <Centered>
        <div role="status" style={{ fontSize: 16, fontWeight: 700, color: 'var(--slate)' }}>
          Confirming your payment…
        </div>
      </Centered>
    );
  }

  if (phase === 'active') {
    return (
      <>
        <div
          role="status"
          style={{
            background: 'var(--teal-tint)',
            color: 'var(--teal-deep)',
            fontSize: 14,
            fontWeight: 800,
            textAlign: 'center',
            padding: '12px 16px',
          }}
        >
          Payment confirmed. Your subscription is active.
        </div>
        <GetTheApp answers={loadAnswers()} claimLink={loadClaimLink()} />
      </>
    );
  }

  const notice = noticeFor(phase);
  return (
    <ClaimNotice kind="patient" title={notice.title} body={notice.body} action={notice.action} />
  );
}

// GA4 `purchase` value for Ads ROAS. Mirrors the live Stripe subscription prices
// (config.stripePriceIdUsd $19.99 / stripePriceIdGbp £14.99). Best-effort: an
// absent/unmapped region falls through to a count-only conversion (no value).
function subscriptionPrice(country: unknown): { value?: number; currency?: string } {
  if (country === 'US') return { value: 19.99, currency: 'USD' };
  if (country === 'GB') return { value: 14.99, currency: 'GBP' };
  return {};
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      {children}
    </div>
  );
}

function RetryCheckoutButton({ label = 'Try checkout again' }: { label?: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div>
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setFailed(false);
          try {
            await beginCheckout();
          } catch {
            setFailed(true);
            setBusy(false);
          }
        }}
        style={{
          background: 'var(--teal)',
          color: '#fff',
          fontSize: 16,
          fontWeight: 800,
          padding: '15px 30px',
          borderRadius: 10,
          border: 'none',
          cursor: busy ? 'wait' : 'pointer',
          boxShadow: 'var(--teal-shadow)',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {busy ? 'Opening secure checkout…' : label}
      </button>
      {failed && (
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--slate)', marginTop: 10 }}>
          We could not start checkout. Please try again, or come back later. Your free trial is unaffected.
        </div>
      )}
    </div>
  );
}

function noticeFor(phase: Exclude<Phase, 'checking' | 'active'>) {
  if (phase === 'open') {
    return {
      title: 'Your checkout was not completed.',
      body: 'No charge was made. Your free trial is still active, so you can pick this up whenever you like.',
      action: <RetryCheckoutButton />,
    };
  }
  if (phase === 'expired') {
    return {
      title: 'That checkout session expired.',
      body: 'No charge was made. Start a fresh checkout below, or just keep using your free trial.',
      action: <RetryCheckoutButton label="Start checkout again" />,
    };
  }
  if (phase === 'processing') {
    return {
      title: 'Payment received. Setting up your access…',
      body: 'Your payment went through and your subscription is being activated. This usually takes under a minute. Refresh this page to check, and if it still shows after a few minutes, email support@expyhealth.com and we will sort it out.',
      action: undefined,
    };
  }
  if (phase === 'missing') {
    return {
      title: 'We could not find that checkout.',
      body: 'This page needs the link Stripe sends you back with after payment. If you paid and landed here anyway, email support@expyhealth.com and we will make it right.',
      action: undefined,
    };
  }
  return {
    title: 'We could not confirm your payment yet.',
    body: 'Something went wrong on our side or with your connection. If you completed payment, your subscription will activate automatically. Refresh in a minute to confirm, or email support@expyhealth.com.',
    action: undefined,
  };
}
